import { useCallback, useEffect, useRef, useState } from "react";
import type { SchemaElement } from "../../../../api/restaurantSchema";
import type { LogicalPoint } from "../../../../components/restaurant-schema/stageLayout";
import { createTableDragGuard } from "../../../../components/restaurant-schema/tableCollision";
import { alignToNeighbours, alignmentGuidesAt, type AlignmentGuide } from "./alignmentGuides";

// Под този праг (в пиксели на екрана) натискането е „докосване" = избор,
// а не местене — пръстът никога не стои идеално неподвижен.
const DRAG_THRESHOLD_PX = 5;
// На толкова пиксела от център/ръб на съсед елементът „залепва" за него.
const ALIGN_THRESHOLD_PX = 6;
// Колко дълго пуснатият елемент стои на новото място от локалното състояние,
// докато оптимистичният запис в кеша на React Query го поеме (без „подскок").
const SETTLE_MS = 400;

type DragSession = {
  elementId: number;
  pointerId: number;
  startClientX: number;
  startClientY: number;
  originX: number;
  originY: number;
  element: SchemaElement;
  moved: boolean;
  // Само за маса: къде застава спрямо съседните маси (плъзга се покрай тях).
  guardTable: ((target: LogicalPoint) => LogicalPoint) | null;
};

export type DragPosition = { elementId: number; x: number; y: number; guides: AlignmentGuide[] };

/**
 * Местене на маси и обекти с Pointer Events — един и същ код за мишка, писалка
 * и пръст. Pointer capture държи събитията при елемента и когато курсорът/пръстът
 * излезе от него; `touch-action: none` на елемента (в SchemaCanvas) спира
 * браузъра да скролва/зуумва, докато се влачи. Елементът може да отиде
 * навсякъде (няма граница); близо до център/ръб на съсед залепва за него и
 * `guides` казва кои помощни линии да се покажат (`alignToNeighbours`). Маса не
 * влиза в друга маса: опира на MIN_TABLE_GAP от нея (на решетката, по-далеч от
 * съседа) и се плъзга покрай ръба ѝ към пръста (`createTableDragGuard`).
 */
export const useElementDrag = ({
  scale,
  elements,
  onMove,
  onSelect,
}: {
  scale: number;
  elements: SchemaElement[];
  onMove: (elementId: number, x: number, y: number) => void;
  onSelect: (elementId: number) => void;
}) => {
  const sessionRef = useRef<DragSession | null>(null);
  const settleTimerRef = useRef<number | null>(null);
  const [dragPosition, setDragPosition] = useState<DragPosition | null>(null);
  const dragPositionRef = useRef<DragPosition | null>(null);

  useEffect(
    () => () => {
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
      }
    },
    [],
  );

  const updatePosition = (position: DragPosition | null) => {
    dragPositionRef.current = position;
    setDragPosition(position);
  };

  const bindElement = useCallback(
    (element: SchemaElement) => ({
      onPointerDown: (event: React.PointerEvent<HTMLElement>) => {
        if (event.pointerType === "mouse" && event.button !== 0) return;
        event.stopPropagation();
        event.currentTarget.setPointerCapture(event.pointerId);
        if (settleTimerRef.current !== null) {
          window.clearTimeout(settleTimerRef.current);
          settleTimerRef.current = null;
        }
        sessionRef.current = {
          elementId: element.id,
          pointerId: event.pointerId,
          startClientX: event.clientX,
          startClientY: event.clientY,
          originX: element.x,
          originY: element.y,
          element,
          moved: false,
          guardTable: element.kind === "table" ? createTableDragGuard(element, elements) : null,
        };
      },
      onPointerMove: (event: React.PointerEvent<HTMLElement>) => {
        const session = sessionRef.current;
        if (!session || session.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - session.startClientX;
        const deltaY = event.clientY - session.startClientY;
        if (!session.moved && Math.hypot(deltaX, deltaY) < DRAG_THRESHOLD_PX) {
          return;
        }
        session.moved = true;
        const aligned = alignToNeighbours(
          session.element,
          session.originX + deltaX / scale,
          session.originY + deltaY / scale,
          elements,
          ALIGN_THRESHOLD_PX / scale,
        );
        const position = session.guardTable ? session.guardTable(aligned) : aligned;
        // Опряла в съседна маса → стои другаде, не където я сложи подравняването:
        // линиите са за мястото, на което наистина е.
        const isHeldBack = position.x !== aligned.x || position.y !== aligned.y;
        updatePosition({
          elementId: session.elementId,
          x: position.x,
          y: position.y,
          guides: isHeldBack
            ? alignmentGuidesAt(session.element, position.x, position.y, elements)
            : aligned.guides,
        });
      },
      onPointerUp: (event: React.PointerEvent<HTMLElement>) => {
        const session = sessionRef.current;
        if (!session || session.pointerId !== event.pointerId) return;
        sessionRef.current = null;
        const dropped = dragPositionRef.current;
        if (!session.moved || !dropped) {
          updatePosition(null);
          onSelect(session.elementId);
          return;
        }
        onSelect(session.elementId);
        if (dropped.x !== session.originX || dropped.y !== session.originY) {
          onMove(session.elementId, dropped.x, dropped.y);
        }
        // Пуснат → помощните линии изчезват веднага, позицията изчаква кеша.
        updatePosition({ ...dropped, guides: [] });
        settleTimerRef.current = window.setTimeout(() => {
          settleTimerRef.current = null;
          updatePosition(null);
        }, SETTLE_MS);
      },
      onPointerCancel: () => {
        sessionRef.current = null;
        updatePosition(null);
      },
    }),
    [scale, elements, onMove, onSelect],
  );

  return { dragPosition, bindElement };
};
